import React, { FC, useState } from "react";
import styles from "./CarPaymentMethod.module.scss";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import {
  Box,
  Button,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslations } from "next-intl";
import Card from "../icons/Card/Card";
import ShieldIcon from "../icons/ShieldIcon/ShieldIcon";
import CustomSearchField from "../forms/Fields/CustomSearchField/CustomSearchField";
import VisaCard from "../icons/VisaCard/VisaCard";
import InfoTooltip from "../icons/InfoTooltip/InfoTooltip";
import { useRouter } from "next/router";
import TermsCard from "../cards/TermsCard/TermsCard";
import AppButton from "../buttons/AppButton/AppButton";
import { Formik, Form } from "formik";
import * as Yup from "yup";

interface CarPaymentMethodProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}

interface FormValues {
  cardNumber: string;
  expiredDate: string;
  cvv: string;
  nameOfCard: string;
  termsAccepted: boolean;
}

const CarPaymentMethod: FC<CarPaymentMethodProps> = ({ setCurrentStep }) => {
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const t = useTranslations();
  const { locale } = useRouter();

  const handleOpenInfoModal = () => setOpenInfoModal(true);
  const handleCloseInfoModal = () => setOpenInfoModal(false);

  const cardPaymentMethods = [
    "/images/payment/visa.png",
    "/images/payment/master.png",
 
  ];

  const paymentMethods = [
    { id: "card", name: t("payment.card"), icon: <Card /> },
  ];

  const validationSchema = Yup.object().shape({
    cardNumber: Yup.string()
      .matches(/^[0-9]{16}$/, t("validation.card_number"))
      .required(t("validation.required")),
    expiredDate: Yup.string()
      .matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/, t("validation.expiry"))
      .required(t("validation.required")),
    cvv: Yup.string()
      .matches(/^[0-9]{3,4}$/, t("validation.cvv"))
      .required(t("validation.required")),
    nameOfCard: Yup.string().required(t("validation.required")),
    termsAccepted: Yup.boolean().oneOf(
      [true],
      t("validation.terms_required")
    ),
  });

  const initialValues: FormValues = {
    cardNumber: "",
    expiredDate: "",
    cvv: "",
    nameOfCard: "",
    termsAccepted: false,
  };

  const handleSubmit = (values: FormValues) => {
    setCurrentStep(5); 
  };

  return (
    <React.Fragment>
      <CustomDialog
        open={openInfoModal}
        onClose={handleCloseInfoModal}
        title={t("CVV Number")}
        content={<div>{t("cvvInfo")}</div>}
        actions={
          <button
            onClick={handleCloseInfoModal}
            className="text-secondary my-2 w-20"
          >
            {t("Ok")}
          </button>
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <Form className="flex flex-col space-y-6 w-full max-c-md:mt-6">
            <Stack direction="row" spacing={1} className="items-center gap-2">
              {paymentMethods.map((method) => (
                <Button
                  key={method.id}
                  variant="outlined"
                  sx={{
                    width: "140px",
                    height: "80px",
                    borderRadius: "6px",
                    border: "1px solid #d8dcde",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <Box sx={{ color: "#333333" }}>{method.icon}</Box>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                    className="!capitalize"
                  >
                    {method.name}
                  </Typography>
                </Button>
              ))}
            </Stack>

      
            <Paper
              sx={{
                backgroundColor: "#eaf5eb",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 3,
                mb: 3,
                boxShadow: "none",
              }}
            >
              <ShieldIcon className="!w-6 !h-6 !min-w-6 !min-h-6" />
              <Typography
                sx={{ fontWeight: 400, color: "#333333", fontSize: "16px" }}
              >
                {t("payment.transactionsSafe")}
              </Typography>
            </Paper>

            <h6 className="font-semibold text-lg">
              {t("payment.enterCardDetails")}
            </h6>
            <div className="flex items-center gap-2">
              <p className="text-[#66797F] text-xs">
                {t("payment.cardsAccepted")}
              </p>
              <div className="flex gap-2">
                {cardPaymentMethods.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt="Payment method"
                    className="h-6 w-6 object-contain rounded border"
                  />
                ))}
              </div>
            </div>

            <CustomSearchField
              name="cardNumber"
              label={t("formGuest.cardNumber")}
              className={`w-full !border-[#e0e0e0] ${
                touched.cardNumber && errors.cardNumber && "!border-red-500"
              }`}
              content={
                <input
                  type="text"
                  name="cardNumber"
                  className={styles.placeholder}
                  value={values.cardNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              }
            />
            {touched.cardNumber && errors.cardNumber && (
              <p className="text-red-500 text-xs">{errors.cardNumber}</p>
            )}

            <div className="flex flex-col md:flex-row gap-4">
              <CustomSearchField
                name="expiredDate"
                label={t("formGuest.expiredDate")}
                className={`w-full !border-[#e0e0e0] ${
                  touched.expiredDate && errors.expiredDate && "!border-red-500"
                }`}
                content={
                  <input
                    type="text"
                    name="expiredDate"
                    placeholder="MM/YY"
                    className={`${styles.placeholder} rtl:text-end`}
                    value={values.expiredDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                  />
                }
              />
              <div className="relative w-full">
                <CustomSearchField
                  name="cvv"
                  label={t("formGuest.cvv")}
                  className={`w-full !border-[#e0e0e0] ${
                    touched.cvv && errors.cvv && "!border-red-500"
                  }`}
                  content={
                    <input
                      type="tel"
                      name="cvv"
                      className={`${styles.placeholder} rtl:text-end`}
                      value={values.cvv}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                    />
                  }
                />
                <Tooltip
                  title={
                    <div className="py-1">
                      <VisaCard xPosition={locale === "en" ? 5 : 18} />
                    </div>
                  }
                  placement="top"
                  arrow
                >
                  <button
                    type="button"
                    className="absolute end-2 top-1/2 -translate-y-1/2 cursor-pointer"
                    onClick={handleOpenInfoModal}
                  >
                    <InfoTooltip className="cursor-pointer" />
                  </button>
                </Tooltip>
              </div>
            </div>

            <CustomSearchField
              name="nameOfCard"
              label={t("formGuest.nameOfCard")}
              className={`w-full !border-[#e0e0e0] ${
                touched.nameOfCard && errors.nameOfCard && "!border-red-500"
              }`}
              content={
                <input
                  type="text"
                  name="nameOfCard"
                  className={styles.placeholder}
                  value={values.nameOfCard}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
              }
            />

            <div className="mt-4 ">
              <TermsCard
                isChecked={values.termsAccepted}
                handleCheckboxChange={() => {
                  setFieldValue("termsAccepted", !values.termsAccepted);
                 }}

              />
              {touched.termsAccepted && errors.termsAccepted && (
                <p className="text-red-500 text-xs mt-2">
                  {errors.termsAccepted}
                </p>
              )}
            </div>

        
            <AppButton
              type="submit"
              label={t("formGuest.confirmBooking")}
              className="md:w-[480px] w-full mt-4 h-12 !rounded-3xl !bg-primary hover:!bg-primary"
              disabled={isSubmitting}
              loading={isSubmitting}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  viewBox="0 0 24 24"
                  fill="none"
                  width="24"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.5 2a2.875 2.875 0 0 0-2.875 2.875V7.75h5.75V4.875A2.875 2.875 0 0 0 12.498 2ZM8.624 4.875V7.75H5c-.69 0-1.25.56-1.25 1.25v10.5c0 .69.56 1.25 1.25 1.25h15c.69 0 1.25-.56 1.25-1.25V9c0-.69-.56-1.25-1.25-1.25h-3.625V4.875a3.875 3.875 0 1 0-7.75 0ZM5 8.75h15a.25.25 0 0 1 .25.25v10.5a.25.25 0 0 1-.25.25H5a.25.25 0 0 1-.25-.25V9A.25.25 0 0 1 5 8.75Zm11.219 3.613a.5.5 0 1 0-.688-.726l-4.408 4.176-1.656-1.553a.5.5 0 0 0-.684.73l2 1.875a.5.5 0 0 0 .686-.002l4.75-4.5Z"
                    fill="#fff"
                  ></path>
                </svg>
              }
            />
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default CarPaymentMethod;
